import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchStorefront, fetchAllServices } from '../api/additionalServicesApi';
import {
  AdditionalServiceSummary,
  StorefrontFilters,
  StorefrontService,
} from '../types/additionalServiceTypes';
import { GeolocationCoords } from './useGeolocation';

export const DEFAULT_STOREFRONT_FILTERS: StorefrontFilters = {
  search: '',
  category: '',
  minPrice: null,
  maxPrice: null,
  radius: 10,
  onlyPromotions: false,
};

interface StorefrontState {
  services: StorefrontService[];
  allServices: AdditionalServiceSummary[];
  loading: boolean;
  error: string | null;
}

export interface UseStorefrontReturn extends StorefrontState {
  filteredServices: StorefrontService[];
  filters: StorefrontFilters;
  setFilters: (updates: Partial<StorefrontFilters>) => void;
  reload: () => void;
}

function applyClientFilters(
  services: StorefrontService[],
  filters: StorefrontFilters,
): StorefrontService[] {
  return services.filter((service) => {
    if (filters.search) {
      const term = filters.search.toLowerCase();
      const matchesName = service.name.toLowerCase().includes(term);
      const matchesDescription = service.description?.toLowerCase().includes(term) ?? false;
      if (!matchesName && !matchesDescription) return false;
    }

    if (filters.category) {
      const categoryLower = filters.category.toLowerCase();
      if (!service.name.toLowerCase().includes(categoryLower)) return false;
    }

    if (filters.minPrice !== null && service.min_price < filters.minPrice) return false;
    if (filters.maxPrice !== null && service.min_price > filters.maxPrice) return false;

    return true;
  });
}

export function useStorefront(coords: GeolocationCoords | null): UseStorefrontReturn {
  const [state, setState] = useState<StorefrontState>({
    services: [],
    allServices: [],
    loading: false,
    error: null,
  });

  const [filters, setFiltersState] = useState<StorefrontFilters>(DEFAULT_STOREFRONT_FILTERS);

  useEffect(() => {
    fetchAllServices()
      .then((data) => setState((prev) => ({ ...prev, allServices: data })))
      .catch(() => {});
  }, []);

  const load = useCallback(async (location: GeolocationCoords, radius: number) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await fetchStorefront({
        latitude: location.latitude,
        longitude: location.longitude,
        radius,
      });
      setState((prev) => ({ ...prev, services: data, loading: false, error: null }));
    } catch {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: 'Não foi possível carregar os serviços disponíveis na sua região',
      }));
    }
  }, []);

  useEffect(() => {
    if (!coords) return;
    load(coords, filters.radius);
  }, [coords, filters.radius, load]);

  const filteredServices = useMemo(
    () => applyClientFilters(state.services, filters),
    [state.services, filters],
  );

  const setFilters = useCallback((updates: Partial<StorefrontFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...updates }));
  }, []);

  const reload = useCallback(() => {
    if (coords) load(coords, filters.radius);
  }, [coords, filters.radius, load]);

  return { ...state, filteredServices, filters, setFilters, reload };
}
